import { useState } from 'react';
import { useTranslation } from 'react-i18next';

interface OmitBU {
  id: string;
  name: string;
  om: number;
  ev: number;
  people: number;
}

interface OmitOU {
  id: string;
  name: string;
  om: number;
  ev: number;
  people: number;
  bus: OmitBU[];
}

interface OmissionsCardProps {
  omitData: OmitOU[];
}

function omitColor(pct: number) {
  if (pct > 12) return '#ef4444';
  if (pct > 7) return '#f59e0b';
  return '#10b981';
}

function downloadCSV(data: OmitOU[], headers: string[]) {
  const rows: string[][] = [headers];
  for (const ou of data) {
    rows.push([ou.name, 'OU', String(ou.people), String(ou.ev), String(ou.om)]);
    for (const bu of ou.bus) {
      rows.push([bu.name, 'BU', String(bu.people), String(bu.ev), String(bu.om)]);
    }
  }
  const csv = rows.map((r) => r.map((c) => `"${c}"`).join(',')).join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'omisiones.csv';
  a.click();
  URL.revokeObjectURL(url);
}

export function OmissionsCard({ omitData }: OmissionsCardProps) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  function toggle(id: string) {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  const csvHeaders = [
    t('analytics.heatmap.unit'),
    t('analytics.summary.omitType'),
    t('analytics.summary.omitPeople'),
    t('analytics.kpi.evaluations'),
    t('analytics.summary.omitRate'),
  ];

  return (
    <div className="au-card">
      <div className="au-card-header">
        <div>
          <div className="au-card-title">
            <i className="fas fa-forward" aria-hidden="true" />
            {t('analytics.summary.omissionsByUnit')}
          </div>
          <div className="au-card-subtitle">{t('analytics.summary.omissionsSubtitle')}</div>
        </div>
        <button
          className="au-omit-excel-btn"
          onClick={() => downloadCSV(omitData, csvHeaders)}
        >
          <i className="fas fa-file-excel" style={{ color: '#217346' }} aria-hidden="true" />
          {t('analytics.summary.downloadExcel')}
        </button>
      </div>
      <div className="au-omit-table-wrap">
        <table className="au-omit-table">
          <thead>
            <tr>
              <th scope="col">{t('analytics.heatmap.unit')}</th>
              <th scope="col">{t('analytics.summary.omitType')}</th>
              <th scope="col">{t('analytics.summary.omitPeople')}</th>
              <th scope="col">{t('analytics.kpi.evaluations')}</th>
              <th scope="col">{t('analytics.summary.omitRate')}</th>
            </tr>
          </thead>
          <tbody>
            {omitData.length === 0 ? (
              <tr>
                <td colSpan={5} className="au-omit-empty">
                  <i className="fas fa-check-circle" style={{ marginRight: 6, color: '#10b981' }} />
                  {t('analytics.summary.noOmissions')}
                </td>
              </tr>
            ) : (
              omitData.map((ou) => {
                const hasBus = ou.bus.length > 0;
                const isOpen = expanded.has(ou.id);
                const omC = omitColor(ou.om);
                return [
                  <tr
                    key={ou.id}
                    className={`omit-ou-row${ou.om > 12 ? ' warning-row' : ''}`}
                    onClick={hasBus ? () => toggle(ou.id) : undefined}
                    style={{ cursor: hasBus ? 'pointer' : 'default' }}
                  >
                    <td>
                      <div className="au-omit-unit-cell">
                        {hasBus ? (
                          <i
                            className="fas fa-chevron-right omit-toggle-icon"
                            style={{ transform: isOpen ? 'rotate(90deg)' : 'none' }}
                            aria-hidden="true"
                          />
                        ) : (
                          <span className="au-omit-indent" />
                        )}
                        <span style={{ fontWeight: 600 }}>{ou.name}</span>
                      </div>
                    </td>
                    <td>
                      <span className="omit-type-badge omit-ou-badge">OU</span>
                    </td>
                    <td className="au-omit-meta">{ou.people}</td>
                    <td className="au-omit-meta">{ou.ev.toLocaleString()}</td>
                    <td>
                      <div className="au-omit-rate-cell">
                        <div className="au-omit-mini-bar">
                          <div
                            className="au-omit-mini-fill"
                            style={{ width: `${Math.min(ou.om, 30) / 30 * 100}%`, background: omC }}
                          />
                        </div>
                        <span style={{ fontWeight: 700, color: omC }}>{ou.om}%</span>
                      </div>
                    </td>
                  </tr>,
                  ...ou.bus.map((bu) => {
                    const buC = omitColor(bu.om);
                    return (
                      <tr
                        key={bu.id}
                        className="omit-bu-row"
                        style={{ display: isOpen ? undefined : 'none' }}
                      >
                        <td>
                          <div className="au-omit-unit-cell au-omit-bu-indent">
                            <i className="fas fa-level-up-alt fa-rotate-90 au-omit-level-icon" aria-hidden="true" />
                            <span style={{ fontWeight: 500, color: '#374151' }}>{bu.name}</span>
                          </div>
                        </td>
                        <td>
                          <span className="omit-type-badge omit-bu-badge">BU</span>
                        </td>
                        <td className="au-omit-meta">{bu.people || '—'}</td>
                        <td className="au-omit-meta">{bu.ev.toLocaleString()}</td>
                        <td>
                          <div className="au-omit-rate-cell">
                            <div className="au-omit-mini-bar">
                              <div
                                className="au-omit-mini-fill"
                                style={{ width: `${Math.min(bu.om, 30) / 30 * 100}%`, background: buC }}
                              />
                            </div>
                            <span style={{ fontWeight: 700, color: buC }}>{bu.om}%</span>
                          </div>
                        </td>
                      </tr>
                    );
                  }),
                ];
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
